// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// Kaizen Dashboard components
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import dayjs from "dayjs";
import dataUsuarios from "../dataUsuarios";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%" },
  overflow: "auto",
  py: 4,
};

const ADD_NOTIFICATION = gql`
  mutation addNotificacion($notificacion: NotificacionInput!) {
    addNotificacion(input: $notificacion) {
      __typename
    }
  }
`;

export default function ModalAdd({ open, handleClose }) {
  const minDate = dayjs().format("YYYY-MM-DD HH:mm");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [notificacion, setNotificacion] = useState({
    titulo: "",
    descripcion: "",
    canal: "app",
    fechaDisponible: minDate,
  });
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "nombreCompleto",
    orden: "ASC",
  });
  const { handleSnackbar } = useContext(MessageManager);
  const [addNotificacion] = useMutation(ADD_NOTIFICATION);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const { columns, getRows } = dataUsuarios;
  const [filter, setFilter] = useState({});

  const [getUsuarios, { data: usuarios, loading }] = useLazyQuery(
    gql`
      query getUsuarios($search: String, $page: Int, $order: Order, $filter: JSON) {
        paginationInfo {
          pages
          total
        }
        usuarios(
          search: $search
          pagination: { page: $page, limit: 10 }
          order: $order
          filter: $filter
        ) {
          id
          nombre
          tipoUser
          apellido
          username
          email
          dni
          sedeCarrera {
            id
            nombre
          }
        }
      }
    `,
    {
      variables: {
        search: search || null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
        filter: filter,
      },
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [getAllUsers, { data: allUsers, loading: loadingAllUsers }] = useLazyQuery(
    gql`
      query getUsuarios($search: String, $order: Order, $filter: JSON) {
        usuarios(search: $search, order: $order, filter: $filter) {
          id
        }
      }
    `,

    {
      variables: {
        search: search || null,
        order: { field: orderBy.campo, order: orderBy.orden },
        filter: filter,
      },
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const { data: sedes } = useQuery(
    gql`
      query getSedes {
        sedes {
          id
          nombre
        }
      }
    `
  );

  const handleSelectUser = (idUser) => {
    if (selectedUsers.includes(idUser)) {
      setSelectedUsers(selectedUsers.filter((user) => user !== idUser));
    } else {
      setSelectedUsers([...selectedUsers, idUser]);
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleChange = (event) => {
    setNotificacion({
      ...notificacion,
      [event.target.name]: event.target.value,
    });
  };

  const handleSave = () => {
    addNotificacion({
      variables: {
        notificacion: {
          header: notificacion.titulo,
          body: notificacion.descripcion,
          channels: notificacion.canal === "app-email" ? ["app", "email"] : [notificacion.canal],
          programmedAt: dayjs(notificacion.fechaDisponible).format("YYYY-MM-DD HH:mm:00"),
          users: selectedUsers,
        },
      },
    })
      .then(() => {
        handleSnackbar("Notificación creada correctamente", "success");
        handleClose();
      })
      .catch((error) => {
        handleSnackbar("Ha ocurrido un error al crear la notificación", "error");
        handleClose();
      });
  };

  useEffect(() => {
    setNotificacion({
      titulo: "",
      descripcion: "",
      canal: "app",
      fechaDisponible: minDate,
    });
    setSelectedUsers([]);
    if (open) {
      getUsuarios();
    }
  }, [open]);

  useEffect(() => {
    if (usuarios) {
      setRows(getRows(usuarios.usuarios, handleSelectUser, selectedUsers));
      setPagesInfo(usuarios.paginationInfo);
    }
  }, [usuarios, selectedUsers]);

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
      >
        <Fade in={open}>
          <Card sx={style}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" px={3}>
              <SoftTypography variant="h6">Nueva notificación</SoftTypography>
            </SoftBox>
            <SoftBox px={3} py={3}>
              <Grid container spacing={2} display={"flex"} alignItems={"flex-start"}>
                <Grid item container spacing={2} xs={12}>
                  <Grid item xs={12} md={12} xxl={6}>
                    <InputLabel htmlFor="Título">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Título
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="Título"
                      placeholder="Ingrese el título de la notificación"
                      type="text"
                      name="titulo"
                      value={notificacion.titulo}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} xxl={3}>
                    <InputLabel htmlFor="Canal">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Canal
                      </SoftTypography>
                    </InputLabel>
                    <Select
                      name="canal"
                      sx={{ cursor: "pointer" }}
                      value={notificacion.canal}
                      onChange={handleChange}
                    >
                      <MenuItem value={"app"}>App</MenuItem>
                      <MenuItem value={"email"}>Email</MenuItem>
                      <MenuItem value={"app-email"}>Email & App</MenuItem>
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={6} xxl={3}>
                    <InputLabel htmlFor="Disponible desde">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Disponible desde
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="Disponible desde"
                      type="datetime-local"
                      name="fechaDisponible"
                      value={notificacion.fechaDisponible}
                      onChange={handleChange}
                      inputProps={{ min: minDate }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="descripcion">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Descripción&nbsp;
                        <SoftTypography variant="caption" fontWeight="light">
                          (Opcional)
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="Descripción"
                      type="text"
                      placeholder="Ingrese la descripción de la notificación"
                      multiline
                      rows={4}
                      name="descripcion"
                      value={notificacion.descripcion}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Card>
                    <SoftBox
                      display={{
                        xs: "flex-row",
                        sm: "flex",
                      }}
                      justifyContent="space-between"
                      alignItems="center"
                      p={3}
                      pb={0}
                    >
                      <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                        <SoftBox display="flex" flexDirection="column" justifyContent="center">
                          <SoftTypography variant="h6">Seleccione los usuarios</SoftTypography>
                          <SoftTypography variant="caption" color="text" fontWeight="light">
                            {`${
                              selectedUsers.length > 0
                                ? `${selectedUsers.length} seleccionado${
                                    selectedUsers.length > 1 ? "s" : ""
                                  }`
                                : "No hay usuarios seleccionados"
                            }`}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox
                          display={{
                            xs: "block",
                            sm: "none",
                          }}
                        >
                          <Tooltip
                            title={
                              selectedUsers.length === allUsers?.usuarios.length
                                ? "Deseleccionar todos"
                                : "Seleccionar todos"
                            }
                            placement="top"
                          >
                            <SoftButton
                              color={
                                selectedUsers.length === allUsers?.usuarios.length
                                  ? "primary"
                                  : "uproGreen"
                              }
                              circular
                              iconOnly
                              disabled={loadingAllUsers}
                              onClick={async () => {
                                const { data } = await getAllUsers({
                                  variables: {
                                    search: search || null,
                                    order: { field: orderBy.campo, order: orderBy.orden },
                                  },
                                });
                                if (selectedUsers.length === data.usuarios.length) {
                                  setSelectedUsers([]);
                                } else {
                                  const allUsers = data.usuarios.map((user) => user.id);
                                  setSelectedUsers(allUsers);
                                }
                              }}
                            >
                              {loadingAllUsers ? (
                                <CircularProgress size={15} color="inherit" />
                              ) : (
                                <Icon>
                                  {selectedUsers.length === allUsers?.usuarios.length
                                    ? "check_box_outline_blank"
                                    : "check_box"}
                                </Icon>
                              )}
                            </SoftButton>
                          </Tooltip>
                        </SoftBox>
                      </SoftBox>
                      <SoftBox
                        display="flex"
                        alignItems="center"
                        justifyContent="end"
                        flexDirection={{
                          xs: "column",
                          sm: "row",
                        }}
                        gap={2}
                        my={{
                          xs: 2,
                          sm: 0,
                        }}
                      >
                        <SoftBox
                          sx={{
                            width: {
                              xs: "100%",
                              sm: "auto",
                            },
                          }}
                        >
                          <SoftInput
                            placeholder="Escribe aquí..."
                            icon={{ component: "search", direction: "left" }}
                            onChange={handleSearch}
                          />
                        </SoftBox>
                        <SoftBox
                          sx={{
                            width: {
                              xs: "100%",
                              sm: "auto",
                            },
                          }}
                        >
                          <Select
                            value={
                              filter?.idSedeCarrera
                                ? filter?.idSedeCarrera
                                : -1
                            }
                            onChange={(e) => {
                              if (e.target.value === -1) {
                                setFilter({
                                  ...filter,
                                  idSedeCarrera: undefined,
                                });
                              } else {
                                setFilter({
                                  ...filter,
                                  idSedeCarrera: e.target.value,
                                });
                              }
                            }}
                            sx={{
                              cursor: "pointer",
                              width: "auto",
                            }}
                          >
                            <MenuItem value={-1}>Todas las sedes</MenuItem>
                            {sedes?.sedes.map((sede) => (
                              <MenuItem key={sede.id} value={sede.id}>
                                {sede.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </SoftBox>
                        <SoftBox
                          sx={{
                            width: {
                              xs: "100%",
                              sm: "auto",
                            },
                          }}
                        >
                          <Select
                            value={
                              filter?.tipoUser
                                ? filter?.tipoUser
                                : filter?.id
                                ? "Solo seleccionados"
                                : -1
                            }
                            onChange={(e) => {
                              if (e.target.value === -1) {
                                setFilter({
                                  ...filter,
                                  tipoUser: undefined,
                                  id: undefined,
                                });
                              } else if (e.target.value === "Solo seleccionados") {
                                setFilter({
                                  ...filter,
                                  id: selectedUsers,
                                  tipoUser: undefined,
                                });
                              } else {
                                setFilter({
                                  ...filter,
                                  tipoUser: e.target.value,
                                  id: undefined,
                                });
                              }
                            }}
                            sx={{
                              cursor: "pointer",
                              width: "auto",
                            }}
                          >
                            <MenuItem value={-1}>Todos los tipos</MenuItem>
                            <MenuItem value="Gestión Académica">Gestión Académica</MenuItem>
                            <MenuItem value="Alumno">Alumno</MenuItem>
                            <MenuItem value="Docente">Docente</MenuItem>
                            <MenuItem value="Administrador">Administrador</MenuItem>
                            <MenuItem value="Solo seleccionados">Solo seleccionados</MenuItem>
                          </Select>
                        </SoftBox>
                        <SoftBox
                          display={{
                            xs: "none",
                            sm: "block",
                          }}
                        >
                          <Tooltip
                            title={
                              selectedUsers.length === allUsers?.usuarios.length
                                ? "Deseleccionar todos"
                                : "Seleccionar todos"
                            }
                            placement="top"
                          >
                            <SoftButton
                              color={
                                selectedUsers.length === allUsers?.usuarios.length
                                  ? "primary"
                                  : "uproGreen"
                              }
                              circular
                              iconOnly
                              disabled={loadingAllUsers}
                              onClick={async () => {
                                const { data } = await getAllUsers({
                                  variables: {
                                    search: search || null,
                                    order: { field: orderBy.campo, order: orderBy.orden },
                                  },
                                });
                                if (selectedUsers.length === data.usuarios.length) {
                                  setSelectedUsers([]);
                                } else {
                                  const allUsers = data.usuarios.map((user) => user.id);
                                  setSelectedUsers(allUsers);
                                }
                              }}
                            >
                              {loadingAllUsers ? (
                                <CircularProgress size={15} color="inherit" />
                              ) : (
                                <Icon>
                                  {selectedUsers.length === allUsers?.usuarios.length
                                    ? "check_box_outline_blank"
                                    : "check_box"}
                                </Icon>
                              )}
                            </SoftButton>
                          </Tooltip>
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox
                      sx={{
                        "& .MuiTableRow-root:not(:last-child)": {
                          "& td": {
                            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          },
                        },
                      }}
                    >
                      {loading ? (
                        <Loading />
                      ) : rows?.length > 0 ? (
                        <DataTable
                          columns={columns}
                          rows={rows}
                          order={orderBy}
                          setOrder={handleOrderBy}
                        />
                      ) : (
                        <SinDatos />
                      )}
                    </SoftBox>
                    <CustomPagination
                      loading={loading}
                      length={usuarios?.usuarios.length}
                      total={pagesInfo?.total}
                      pages={pagesInfo?.pages}
                      page={page}
                      setPage={setPage}
                      noCard
                    />
                  </Card>
                </Grid>
              </Grid>
            </SoftBox>
            <SoftBox display="flex" justifyContent="flex-end" alignItems="center" px={3}>
              <SoftBox mr={2}>
                <SoftButton variant="gradient" color="dark" onClick={handleClose} circular>
                  Cancelar
                </SoftButton>
              </SoftBox>
              <SoftBox>
                <SoftButton
                  variant="gradient"
                  color="primary"
                  onClick={handleSave}
                  disabled={
                    notificacion.titulo === "" ||
                    notificacion.fechaDisponible === "" ||
                    selectedUsers.length === 0 ||
                    notificacion.fechaDisponible === ""
                  }
                  circular
                >
                  Enviar&nbsp;&nbsp;
                  <Icon>send</Icon>
                </SoftButton>
              </SoftBox>
            </SoftBox>
          </Card>
        </Fade>
      </Modal>
    </>
  );
}

ModalAdd.defaultProps = {
  recordatorio: {
    titulo: "",
    descripcion: "",
    fechaVencimiento: "",
  },
};

ModalAdd.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  idUser: PropTypes.string,
};
